// src/components/shipment/AIInsightCard.jsx
import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

// ─── Accent System ────────────────────────────────────────────────────────────
const accent = {
    blue: {
        beam: "#3b82f6",
        light: {
            bar: "bg-blue-500",
            iconBg: "bg-blue-50",
            iconText: "text-blue-600",
            iconBorder: "border-blue-200",
            trendBg: "bg-blue-50",
            trendText: "text-blue-700",
        },
        dark: {
            bar: "bg-blue-400",
            iconBg: "dark:bg-blue-950",
            iconText: "dark:text-blue-400",
            iconBorder: "dark:border-blue-800",
            trendBg: "dark:bg-blue-950",
            trendText: "dark:text-blue-400",
        },
    },
    emerald: {
        beam: "#10b981",
        light: {
            bar: "bg-emerald-500",
            iconBg: "bg-emerald-50",
            iconText: "text-emerald-600",
            iconBorder: "border-emerald-200",
            trendBg: "bg-emerald-50",
            trendText: "text-emerald-700",
        },
        dark: {
            bar: "bg-emerald-400",
            iconBg: "dark:bg-emerald-950",
            iconText: "dark:text-emerald-400",
            iconBorder: "dark:border-emerald-800",
            trendBg: "dark:bg-emerald-950",
            trendText: "dark:text-emerald-400",
        },
    },
    amber: {
        beam: "#f59e0b",
        light: {
            bar: "bg-amber-500",
            iconBg: "bg-amber-50",
            iconText: "text-amber-600",
            iconBorder: "border-amber-200",
            trendBg: "bg-amber-50",
            trendText: "text-amber-700",
        },
        dark: {
            bar: "bg-amber-400",
            iconBg: "dark:bg-amber-950",
            iconText: "dark:text-amber-400",
            iconBorder: "dark:border-amber-800",
            trendBg: "dark:bg-amber-950",
            trendText: "dark:text-amber-400",
        },
    },
    red: {
        beam: "#ef4444",
        light: {
            bar: "bg-red-500",
            iconBg: "bg-red-50",
            iconText: "text-red-600",
            iconBorder: "border-red-200",
            trendBg: "bg-red-50",
            trendText: "text-red-700",
        },
        dark: {
            bar: "bg-red-400",
            iconBg: "dark:bg-red-950",
            iconText: "dark:text-red-400",
            iconBorder: "dark:border-red-800",
            trendBg: "dark:bg-red-950",
            trendText: "dark:text-red-400",
        },
    },
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const AIInsightCard = ({
    icon: Icon,
    title,
    value,
    trend,
    trendUp,
    accentKey = "blue",
    variant = "default", // ✅ NEW
}) => {
    const a = accent[accentKey] ?? accent.blue;
    const TrendIcon = trendUp ? ArrowUpRight : ArrowDownRight;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="group relative overflow-hidden rounded-xl p-[1px]
                 transition-all duration-200 ease-out
                 hover:shadow-lg hover:-translate-y-0.5
                 dark:hover:shadow-slate-800/50"
        >
            {/* 🔹 Rotating beam border */}
            <span
                aria-hidden
                className="pointer-events-none absolute inset-[-300%]
                   animate-[spin_7s_linear_infinite]
                   bg-[conic-gradient(from_0deg,transparent_0deg,transparent_260deg,var(--beam-color)_280deg,transparent_360deg)]"
                style={{ "--beam-color": a.beam }}
            />

            {/* 🔹 Inner background */}
            <span
                aria-hidden
                className="pointer-events-none absolute inset-[1px] rounded-[11px]
                   bg-white dark:bg-slate-900"
            />

            {/* 🔹 Card content */}
            <div
                className={`relative z-10 rounded-[11px]
                    border border-slate-200 dark:border-slate-800
                    bg-white dark:bg-slate-900
                    ${variant === "compact" ? "p-3" : "p-5"}`}
            >
                {/* Hover accent bar */}
                <span
                    className={`absolute inset-x-0 top-0 h-[3px] rounded-t-[11px]
                      ${a.light.bar} opacity-0
                      group-hover:opacity-100 transition-opacity duration-200`}
                />

                {variant === "compact" ? (
                    // ✅ COMPACT LAYOUT
                    <div className="flex items-center gap-3">
                        {/* Icon */}
                        <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center
                          border
                          ${a.light.iconBg} ${a.light.iconText} ${a.light.iconBorder}
                          ${a.dark.iconBg} ${a.dark.iconText} ${a.dark.iconBorder}`}
                        >
                            <Icon className="w-4 h-4" />
                        </div>

                        {/* Value + Title */}
                        <div className="flex flex-col">
                            <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                                {value}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                {title}
                            </span>
                        </div>
                    </div>
                ) : (
                    // ✅ DEFAULT LAYOUT
                    <>
                        {/* Icon + Trend */}
                        <div className="flex items-center justify-between">
                            <div
                                className={`w-11 h-11 rounded-xl flex items-center justify-center
                            border-2
                            ${a.light.iconBg} ${a.light.iconText} ${a.light.iconBorder}
                            ${a.dark.iconBg} ${a.dark.iconText} ${a.dark.iconBorder}`}
                            >
                                <Icon className="w-5 h-5" strokeWidth={2.2} />
                            </div>

                            {trend && (
                                <span
                                    className={`inline-flex items-center gap-1 px-2 h-6 rounded-full
                              text-[11px] font-semibold
                              ${a.light.trendBg} ${a.light.trendText}
                              ${a.dark.trendBg} ${a.dark.trendText}`}
                                >
                                    <TrendIcon className="w-3 h-3" strokeWidth={2.5} />
                                    {trend}
                                </span>
                            )}
                        </div>

                        {/* Value */}
                        <div className="mt-4 text-[28px] font-bold text-slate-900 dark:text-white tabular-nums leading-none tracking-tight">
                            {value}
                        </div>

                        {/* Title */}
                        <div className="mt-1.5 text-[13px] font-semibold text-slate-800 dark:text-slate-200">
                            {title}
                        </div>

                        {/* Subtext */}
                        {trend && (
                            <div className="mt-1 text-[11.5px] text-slate-500 dark:text-slate-400">
                                {trend} vs last month
                            </div>
                        )}
                    </>
                )}
            </div>
        </motion.div>
    );
};

export default AIInsightCard;

// ─── Usage Example ────────────────────────────────────────────────────────────
// Replace your insightCards useMemo with this:

/*
const insightCards = useMemo(
  () => [
    {
      icon: Package,
      title: t("totalShipments"),
      value: summary?.total ?? 0,
      trend: "+12%",
      trendUp: true,
      accentKey: "blue",
    },
    {
      icon: CheckCircle,
      title: t("active"),
      value: summary?.active ?? 0,
      trend: t("onTrack"),
      trendUp: true,
      accentKey: "emerald",
    },
    {
      icon: Truck,
      title: t("inTransit"),
      value: summary?.intransit ?? 0,
      trend: "3 arriving",
      trendUp: true,
      accentKey: "amber",
    },
    {
      icon: AlertCircle,
      title: t("needsAttention"),
      value: summary?.needattension ?? 0,
      trend: t("reviewRequired"),
      trendUp: false,
      accentKey: "red",
    },
  ],
  [summary, t]
);

// Render:
<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
  {insightCards.map((card) => (
    <AIInsightCard key={card.title} {...card} />
  ))}
</div>
*/